/**
 * Frontend detection of an already-invalid server session.
 * The server remains the auth SSOT; polling /isExpired must not extend expires_at.
 */
import { getUserInfo, getSavedCredentials, saveCredentials } from "./localStorage";
import { isTripMasterSurface } from "../surfaces/tripMaster/tripMasterEnv";
import {
  TRIPMASTER_ACCESS_STORAGE_KEY,
} from "../features/vacationPackagesPublic/tripMasterAccessLogic";

export const SESSION_POLL_INTERVAL_MS = 45000;

const INVALID_SESSION_CODES = new Set([
  "session_invalid_or_inactive",
  "session_replaced",
  "session_expired",
  "token_expired",
  "expired",
]);

const IGNORE_PATH_SNIPPETS = [
  "/login",
  "/register",
  "/resetpassword",
  "/changepassword",
  "/auth/verify-login-code",
  "/auth/resend-login-code",
  "/logout",
];

let inFlightLogout = null;

export function hasLocalSession() {
  try {
    const ls = typeof localStorage !== "undefined" ? localStorage.getItem("sessionID") : null;
    if (ls && String(ls).trim()) return true;
    const user = getUserInfo();
    if (user && typeof user === "object") {
      const sid = user.sessionID ?? user.sessionId;
      if (sid != null && String(sid).trim()) return true;
    }
  } catch {
    /* ignore */
  }
  return false;
}

export function parseResponseData(data) {
  if (data == null) return null;
  if (typeof Blob !== "undefined" && data instanceof Blob) return null;
  if (typeof ArrayBuffer !== "undefined" && data instanceof ArrayBuffer) return null;
  if (typeof data === "string") {
    const trimmed = data.trim();
    if (!trimmed) return null;
    try {
      return JSON.parse(trimmed);
    } catch {
      return null;
    }
  }
  if (typeof data === "object") return data;
  return null;
}

function pathFromUrl(url) {
  const raw = String(url || "");
  try {
    if (raw.includes("://")) {
      return new URL(raw).pathname.toLowerCase();
    }
  } catch {
    /* ignore */
  }
  return raw.split("?")[0].toLowerCase();
}

export function shouldIgnoreInvalidSessionUrl(url) {
  const path = pathFromUrl(url);
  return IGNORE_PATH_SNIPPETS.some((snippet) => path.includes(snippet));
}

function collectCodeStrings(payload) {
  if (!payload || typeof payload !== "object") return [];
  const values = [
    payload.error,
    payload.code,
    payload.error_code,
    payload.reason,
    payload.status,
    payload?.error?.code,
    payload?.error?.error,
    payload?.data?.error,
    payload?.data?.code,
  ];
  return values
    .map((value) => {
      if (value == null) return "";
      if (typeof value === "object") {
        return String(value.code || value.error || "").trim().toLowerCase();
      }
      return String(value).trim().toLowerCase();
    })
    .filter(Boolean);
}

function isSessionValidityEndpoint(url) {
  return pathFromUrl(url).includes("/isexpired");
}

export function responseIndicatesInvalidSession({ status, data, url, hasSession } = {}) {
  const localSession = hasSession ?? hasLocalSession();
  if (!localSession) return false;
  if (shouldIgnoreInvalidSessionUrl(url)) return false;

  const payload = parseResponseData(data);
  const httpStatus = Number(status || 0);
  const codes = collectCodeStrings(payload);
  const sessionCheck = isSessionValidityEndpoint(url);

  // Confirmed server session death only — a bare 401 from a business/admin
  // API (missing route, missing admin key, entitlement) must not log out.
  if (codes.some((code) => INVALID_SESSION_CODES.has(code))) return true;
  if (sessionCheck && payload && payload.active === false) return true;
  if (sessionCheck && httpStatus === 401) return true;
  return false;
}

function readPreservedTripMasterGate() {
  try {
    if (!isTripMasterSurface()) return null;
    return localStorage.getItem(TRIPMASTER_ACCESS_STORAGE_KEY);
  } catch {
    return null;
  }
}

function restorePreservedTripMasterGate(value) {
  if (!value) return;
  try {
    localStorage.setItem(TRIPMASTER_ACCESS_STORAGE_KEY, value);
  } catch {
    /* ignore */
  }
}

export function redirectToLoginIfNeeded() {
  try {
    if (isTripMasterSurface()) return;
    const path = String(window.location?.pathname || "").toLowerCase();
    if (path === "/login" || path.startsWith("/login/")) return;
    window.location.assign("/login");
  } catch {
    /* ignore */
  }
}

export function clearLocalSessionState() {
  const savedCredentials = getSavedCredentials();
  const tripMasterGate = readPreservedTripMasterGate();
  try {
    localStorage.removeItem("agentLastActivityTime");
    localStorage.removeItem("sessionID");
    localStorage.removeItem("agentId");
    localStorage.clear();
    sessionStorage.clear();
  } catch {
    /* ignore */
  }
  restorePreservedTripMasterGate(tripMasterGate);
  if (savedCredentials && savedCredentials.email && savedCredentials.password) {
    saveCredentials(savedCredentials.email, savedCredentials.password);
  }
}

export async function triggerInvalidSessionLogout() {
  if (inFlightLogout) return inFlightLogout;
  inFlightLogout = (async () => {
    const tripMasterGate = readPreservedTripMasterGate();
    try {
      const { logout } = await import("./authService");
      await logout();
    } catch {
      clearLocalSessionState();
    }
    restorePreservedTripMasterGate(tripMasterGate);
    redirectToLoginIfNeeded();
  })();
  return inFlightLogout;
}

export function resetInvalidSessionHandlingForTests() {
  inFlightLogout = null;
}

export function notifyIfInvalidSession({ status, data, url, hasSession } = {}) {
  if (!responseIndicatesInvalidSession({ status, data, url, hasSession })) {
    return Promise.resolve(false);
  }
  return triggerInvalidSessionLogout().then(() => true);
}

export function notifyIfNativeFetchResponse(res, data) {
  if (!res) return Promise.resolve(false);
  return notifyIfInvalidSession({
    status: res.status,
    data,
    url: res.url,
  });
}

export function startSessionValidityPolling({
  intervalMs = SESSION_POLL_INTERVAL_MS,
  checkSession,
} = {}) {
  const runCheck =
    checkSession ||
    (async () => {
      const { isAuthenticated } = await import("./authService");
      return isAuthenticated();
    });

  if (isTripMasterSurface()) {
    return () => {};
  }

  const tick = () => {
    if (!hasLocalSession()) return;
    if (isTripMasterSurface()) return;
    const path = String(window.location?.pathname || "").toLowerCase();
    if (path === "/login" || path.startsWith("/login/")) return;
    void runCheck();
  };

  const id = setInterval(tick, intervalMs);
  return () => clearInterval(id);
}